import { Injectable, inject } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { ENVIRONMENT } from '@models';
import { BehaviorSubject } from 'rxjs';
import { safeLocalStorage } from './branding-cache';
import {
  UpdateManifest,
  createBusyCounter,
  createKeyedSingleFlight,
  evaluateOtaManifest,
  failedVersionFromEvent,
  findDownloadedBundle,
  MANIFEST_REQUEST_TIMEOUT_MS,
  manifestRequestUrl,
  parseManifestResponse,
  readFailedOtaVersions,
  rememberFailedOtaVersion,
  requiresNativeCheck,
} from './update-decision';

export type { UpdateManifest } from './update-decision';

/**
 * Self-hosted OTA update service using @capgo/capacitor-updater.
 *
 * This service ONLY downloads. Activation happens in `apps/app/src/main.ts`, before
 * Angular (and therefore Firestore) exists: it `set()`s the highest downloaded
 * bundle newer than the running one (`pickBundleToActivate`). On iOS, reloading the
 * WebView into a new bundle while Firestore is open leaves its IndexedDB database
 * locked (home stuck "loading") — both capgo's `next()`/`installNext` and our own
 * `terminate()` + `set()` hung on device. So this service never calls `set()` nor
 * `next()` (to force an update: `minVersion`).
 *
 * 1. `notifyReady()` — APP_INITIALIZER: `notifyAppReady()` so capgo does not roll back.
 * 2. `init()` — on startup and on every `resume`: `downloadUpdate()`.
 * 3. `downloadUpdate()` — also the `/version` button.
 *
 * Guards (see `update-decision.ts`):
 * - Only a manifest whose `version` is STRICTLY greater than the running bundle
 *   (`environment.liveVersion ?? environment.version`) is accepted.
 * - `minNativeVersion` (optional): ignored when `App.getInfo().version` is lower
 *   or cannot be read (`"1.0"` is normalised to `"1.0.0"` first).
 * - Versions that already failed to boot on this device are never downloaded
 *   again. capgo rolls back a bundle that does not call `notifyAppReady` and
 *   deletes it (`autoDeleteFailed`), but `download()` accepts that version again:
 *   without this memory it would be re-downloaded and re-activated on every launch.
 *   capgo exposes the failure once through `getFailedUpdate()`; the version is kept
 *   in localStorage (same WebView origin for every bundle).
 *
 * The manifest is fetched with `CapacitorHttp` (native HTTP layer, no CORS): the
 * tenant buckets have no CORS configuration. Its URL is per-deployment
 * (`environment.liveUpdate.manifestUrl`); there is no global fallback.
 *
 * No updates run on web (Capacitor.isNativePlatform() guard).
 */
@Injectable({ providedIn: 'root' })
export class UpdateService {
  private readonly environment = inject<{ version?: string; liveVersion?: string } | null>(ENVIRONMENT, {
    optional: true,
  });

  /** True while an update check/download is running. */
  readonly downloading$ = new BehaviorSubject<boolean>(false);

  /** In-memory copy of the failed OTA versions (protects even if storage writes fail). */
  private failedVersions: string[] = [];

  /** Memoised once per JS runtime: capgo reloads the WebView after every rollback. */
  private failureSync: Promise<void> | null = null;

  /** One download per version at a time (background check vs `/version` button). */
  private readonly downloads = createKeyedSingleFlight<unknown>();

  /** Drives `downloading$` across overlapping calls. */
  private readonly busy = createBusyCounter();

  /**
   * Step 1 — must be called as an APP_INITIALIZER.
   * Tells the native plugin the app loaded successfully so it doesn't roll back.
   */
  async notifyReady(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      await CapacitorUpdater.notifyAppReady();
    } catch (e) {
      console.error('[UpdateService] notifyAppReady failed', e);
    }
  }

  /**
   * Step 2 — download checks on startup and on every `resume`.
   * Call once after app startup (e.g. from AppComponent.ngOnInit).
   * @param manifestUrl Per-deployment latest.json manifest URL
   *   (`environment.liveUpdate.manifestUrl`).
   */
  async init(manifestUrl: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    await this.downloadUpdate(manifestUrl);
    App.addListener('resume', () => {
      this.downloadUpdate(manifestUrl).catch(() => {});
    });
  }

  /**
   * Checks whether an applicable OTA bundle exists without downloading it.
   * Returns the manifest when it is applicable (strictly newer than the running
   * bundle and compatible with the native binary), or `null` on web, without a
   * `manifestUrl` (OTA disabled for this build), or when nothing applies.
   */
  async checkForUpdate(manifestUrl: string): Promise<UpdateManifest | null> {
    if (!Capacitor.isNativePlatform() || !manifestUrl) return null;

    return this.fetchApplicableManifest(manifestUrl);
  }

  /**
   * Downloads the applicable OTA bundle when it is not on the device yet. NEVER
   * activates it: `main.ts` does on the next launch. Returns `true` when the
   * bundle is on the device, `false` on web, without a `manifestUrl`, with no
   * applicable manifest, or on failure. Never throws.
   */
  async downloadUpdate(manifestUrl: string): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || !manifestUrl) return false;

    this.downloading$.next(this.busy.begin());
    try {
      const manifest = await this.fetchApplicableManifest(manifestUrl);
      if (!manifest) return false;
      if (findDownloadedBundle(await CapacitorUpdater.list(), manifest.version, manifest.checksum)) return true;
      await this.downloads.run(manifest.version, () =>
        CapacitorUpdater.download({
          url: manifest.url,
          version: manifest.version,
          ...(manifest.checksum ? { checksum: manifest.checksum } : {}),
        }),
      );
      return true;
    } catch (e) {
      // Non-fatal: update failures should never crash the app
      console.warn('[UpdateService] OTA download failed', e);
      return false;
    } finally {
      this.downloading$.next(this.busy.end());
    }
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  /**
   * Fetches the manifest and returns it only when `evaluateOtaManifest` accepts
   * it; `null` otherwise. The native version is read only when the manifest
   * declares `minNativeVersion`.
   */
  private async fetchApplicableManifest(manifestUrl: string): Promise<UpdateManifest | null> {
    // Must complete BEFORE deciding: the failure recorded by capgo on the previous
    // runtime is what stops a rolled-back version from being downloaded again.
    await this.syncFailedUpdates();

    const [manifest, activeBundleVersion] = await Promise.all([
      this.fetchManifest(manifestUrl),
      this.getActiveBundleVersion(),
    ]);
    const nativeVersion = requiresNativeCheck(manifest) ? await this.getNativeVersion() : null;

    const decision = evaluateOtaManifest({
      manifest,
      runningVersion: this.runningBundleVersion(),
      nativeVersion,
      activeBundleVersion,
      failedVersions: this.failedVersions,
    });

    if (!decision.applicable) {
      if (!['no-manifest', 'not-newer', 'already-active'].includes(decision.reason)) {
        console.warn(`[UpdateService] OTA manifest ignored: ${decision.reason}`);
      }
      return null;
    }
    return manifest;
  }

  /**
   * Loads the persisted failed versions, consumes capgo's `getFailedUpdate()`
   * (natively persisted, cleared after first read) and subscribes to
   * `updateFailed`. Runs once per JS runtime; never throws.
   */
  private syncFailedUpdates(): Promise<void> {
    if (!this.failureSync) {
      this.failureSync = (async () => {
        this.failedVersions = readFailedOtaVersions(safeLocalStorage());
        try {
          this.recordFailedVersion(failedVersionFromEvent(await CapacitorUpdater.getFailedUpdate()));
        } catch (e) {
          console.warn('[UpdateService] getFailedUpdate failed', e);
        }
        try {
          await CapacitorUpdater.addListener('updateFailed', (event) => {
            this.recordFailedVersion(failedVersionFromEvent(event));
          });
        } catch (e) {
          console.warn('[UpdateService] updateFailed listener failed', e);
        }
      })();
    }
    return this.failureSync;
  }

  private recordFailedVersion(version: string | null): void {
    if (!version) return;
    console.warn(`[UpdateService] OTA bundle ${version} failed to boot; it will not be retried`);
    const persisted = rememberFailedOtaVersion(safeLocalStorage(), version);
    // Keep whatever was already in memory even if storage is unavailable.
    this.failedVersions = [...new Set([...this.failedVersions, ...persisted])];
  }

  /** Version of the web bundle currently running (compiled into this bundle). */
  private runningBundleVersion(): string | null {
    return this.environment?.liveVersion ?? this.environment?.version ?? null;
  }

  /** Version capgo reports for the active bundle, or `null` when unavailable. */
  private async getActiveBundleVersion(): Promise<string | null> {
    try {
      const current = await CapacitorUpdater.current();
      return current.bundle?.version ?? null;
    } catch {
      return null;
    }
  }

  /** Native binary version (`App.getInfo().version`), or `null` when unavailable. */
  private async getNativeVersion(): Promise<string | null> {
    try {
      const info = await App.getInfo();
      return info?.version ?? null;
    } catch (e) {
      console.warn('[UpdateService] App.getInfo failed', e);
      return null;
    }
  }

  /**
   * Fetches `latest.json` through the native HTTP layer (`CapacitorHttp`, not
   * subject to CORS). No request headers (a `Cache-Control` header would only
   * add a preflight on web); a `?t=<now>` query parameter defeats intermediate
   * caches instead. Any failure or unexpected response shape → `null`.
   * Only reached on native platforms (every public entry point is guarded).
   */
  private async fetchManifest(url: string): Promise<UpdateManifest | null> {
    const requestUrl = manifestRequestUrl(url, Date.now());
    if (!requestUrl) return null;
    try {
      return parseManifestResponse(
        await CapacitorHttp.get({
          url: requestUrl,
          connectTimeout: MANIFEST_REQUEST_TIMEOUT_MS,
          readTimeout: MANIFEST_REQUEST_TIMEOUT_MS,
        }),
      );
    } catch {
      return null;
    }
  }
}
